import { useSpace } from '@ably/spaces/react';
import {
  faChevronDown,
  faChevronRight,
  faEllipsis,
  faEllipsisVertical,
  faInfoCircle,
  faLink,
  faPlus,
  faRefresh,
  faSpinner,
  faTable,
  faTimes,
  faToilet,
  faTrash,
  faUpload
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from '@tanstack/react-query';
import { Message } from 'ably';
import {
  ColDef,
  colorSchemeDark,
  ColumnState,
  GridReadyEvent,
  IRowNode,
  ModuleRegistry,
  RowDataTransaction,
  AllCommunityModule,
  themeBalham
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { AxiosError } from 'axios';
import axios from 'axios';
import type { JSX } from 'react';
import { useContext, useEffect, useMemo, useRef, useCallback, useState } from 'react';

import { billingItemsChannelName } from '@/channels/utils/channelNames';
import PageHeader from '@/components/PageHeader';
import AppContext from '@/contexts/appContext';
import { useChannelData } from '@/hooks/useChannel';
import useDugoutBillingRowSelection from '@/hooks/useDugoutBillingRowSelection';
import { createBillingRowColumnDefs } from '@/pages/BillingReportResults/columnDefs';
import AssociateServiceLines from '@/pages/BillingReportResults/components/AssociateServiceLines';
import BillingReportFilters from '@/pages/BillingReportResults/components/BillingReportFilters';
import FilterExplanation from '@/pages/BillingReportResults/components/FilterExplanation';
import IgnoreErrorServiceLines from '@/pages/BillingReportResults/components/IgnoreErrorServiceLines';
import ManualHoldServiceLines from '@/pages/BillingReportResults/components/ManualHoldServiceLines';
import RowEditor from '@/pages/BillingReportResults/components/RowEditor';
import { AssociateBillingRowsProvider } from '@/pages/BillingReportResults/contexts/AssociateBillingRowsContext';
import { BillingFlagReasonProvider } from '@/pages/BillingReportResults/contexts/BillingFlagReasonContext';
import { BillingReportProvider } from '@/pages/BillingReportResults/contexts/BillingReportProvider';
import BillingItemFlagRepository from '@/pages/BillingReportResults/repositories/BillingItemFlagRepository';
import {
  AlertMessageCount,
  BillingReportEvent,
  BillingReportRow,
  UiMessage,
  isRefreshCompleteEvent,
  isRefreshFailedEvent,
  isRefreshStartedEvent,
  isSubmitCompleteEvent,
  isSubmitFailedEvent,
  isSubmitStartedEvent
} from '@/pages/BillingReportResults/types';
import {
  BillingItemFlagType,
  BulkBillingItemFlagPayload,
  BulkIgnoreErrorsFlag
} from '@/pages/BillingReportResults/types/billing_item_flag';
import { Cms1500 } from '@/pages/BillingReportResults/types/cms_1500';
import { LinkedServiceLine } from '@/pages/BillingReportResults/types/linked_service';
import BillingItemRepository from '@/repositories/BillingItemRepository';
import AvatarStackForSpace from '@/ui/components/AvatarStackForSpace';
import Button from '@/ui/components/Button';
import Dropdown from '@/ui/components/Dropdown';
import Tooltip from '@/ui/components/Tooltip';
import { Paginator } from '@/ui/Paginator/Paginator';
import { healthieLink } from '@/utils/healthieLinks';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

import AlertSummaryPanel from './components/AlertSummaryPanel';
import {
  alertFilterFromSearch,
  alertFilterParams,
  alertFilterToSearch,
  billingItemIdFilterFromSearch,
  filterModelFromSearch,
  gridStateToSearch,
  pageFromSearch,
  pageToSearch,
  rowMatchesFilterModel,
  sortModelFromSearch,
  translateFilterModel,
  translateSortModel
} from './filters';
import type { AlertFilter, FilterModel, SortModelItem } from './filters';
import './style.css';

ModuleRegistry.registerModules([AllCommunityModule]);

// Server error payloads' `error` field can be a plain string or a DRF
// field-errors dict -- interpolating the latter directly into a template
// literal coerces it to "[object Object]", so stringify non-string values.
const extractErrorDetail = (error: AxiosError): string => {
  const serverError = (error.response?.data as { error?: unknown } | undefined)?.error;
  if (typeof serverError === 'string') return serverError;
  if (serverError) return JSON.stringify(serverError);
  return error.message;
};

/**
 * Cross-report view of BillingItem rows. Renders each row with the same column
 * definitions and cell renderers as the billing report grid (BillingReportResultApp),
 * reused via createBillingRowColumnDefs, so a row looks identical in both places.
 * Also supports selection-based bulk actions (hold, ignore error, associate,
 * edit, submit, refresh, remove) that resolve report context server-side.
 */
const BillingItemsList = (): JSX.Element => {
  const { addMessage, host, currentUser, resolvedTheme } = useContext(AppContext);
  const billingItemRepo = BillingItemRepository();
  const billingItemFlagRepository = BillingItemFlagRepository(host);
  const gridRef = useRef<AgGridReact<BillingReportRow>>(null);

  const { space } = useSpace();
  const { showSelectedRowInDugout } = useDugoutBillingRowSelection();

  useEffect(() => {
    if (space && currentUser) {
      space.enter({
        name: currentUser.first_name + ' ' + currentUser.last_name,
        email: currentUser.email,
        avatar_url: currentUser.avatar_url
      });
    }
    return () => {
      space?.leave();
    };
  }, [space, currentUser]);

  const [page, setPage] = useState(() => pageFromSearch(window.location.search));
  const [sortModel, setSortModel] = useState<SortModelItem[]>(() =>
    sortModelFromSearch(window.location.search)
  );

  // Restored from the URL on load (above) and written back on every change,
  // so a page other than the first is reloadable and shareable as a link.
  useEffect(() => {
    const url = new URL(window.location.href);
    const search = pageToSearch(url.search, page);
    if (search === url.searchParams.toString()) return;

    url.search = search;
    window.history.replaceState({}, '', url.toString());
  }, [page]);

  const [selectedRows, setSelectedRows] = useState<BillingReportRow[]>([]);
  const [hasSelection, setHasSelection] = useState(false);

  const [showManualHoldInput, setShowManualHoldInput] = useState(false);
  const [showIgnoreErrorInput, setShowIgnoreErrorInput] = useState(false);
  const [showAssociateClaimPanel, setShowAssociateClaimPanel] = useState(false);
  const [editingSelectedRows, setEditingSelectedRows] = useState(false);
  const [linkedServiceLines, setLinkedServiceLines] = useState<LinkedServiceLine[]>([]);

  const [loading, setLoading] = useState({
    holding: false,
    clearingHold: false,
    ignoringErrors: false,
    associating: false,
    deleting: false,
    refreshing: false,
    submitting: false,
    flushingLookups: false
  });

  const setErrorMessage = useCallback(
    (message: string) => {
      addMessage({ level: 'error', text: message });
    },
    [addMessage]
  );

  const setMessages = useCallback(
    (messages: UiMessage[]) => {
      messages.forEach((message) => {
        addMessage({
          level:
            message.type === 'error' ? 'error' : message.type === 'success' ? 'success' : 'info',
          text: typeof message.message === 'string' ? message.message : String(message.message),
          action_text: message.actionText,
          on_action: message.onAction,
          // Keep an actionable toast (e.g. Saved Edit / Undo) on screen instead
          // of letting it auto-dismiss in 3s before the user can click it.
          persistent: !!message.onAction
        });
      });
    },
    [addMessage]
  );

  const popupParent = useMemo<HTMLElement | undefined>(
    () => (typeof document !== 'undefined' ? document.body : undefined),
    []
  );

  // Matches the compact sizing the grid previously got from the
  // now-removed `.custom-billing-theme` CSS variable overrides.
  const gridTheme = useMemo(
    () =>
      themeBalham.withParams({
        spacing: 5,
        rowHeight: 40,
        headerHeight: 44,
        wrapperBorderRadius: 0
      }),
    []
  );

  const [showAlertSummary, setShowAlertSummary] = useState(false);

  // Translated column filters, kept in state so the alert summary query can key
  // off them -- the grid's own filter model lives outside React.
  const [gridFilters, setGridFilters] = useState<Record<string, string>>(() =>
    translateFilterModel({
      ...filterModelFromSearch(window.location.search),
      ...billingItemIdFilterFromSearch(window.location.search)
    })
  );

  // Click-to-filter from the Alert Summary panel. Held outside the grid's filter
  // model because it isn't a column filter -- `errors`/`warnings` are
  // dict-shaped and can't be filtered server-side, so this narrows by
  // BillingAlert id instead (the `alert_ids` param).
  //
  // Restored from the URL on load, and written back on every change, so
  // filtering by an alert is reloadable and shareable as a link.
  const initialAlertFilter = useMemo(() => alertFilterFromSearch(window.location.search), []);
  const [alertFilter, setAlertFilter] = useState<AlertFilter | null>(initialAlertFilter);

  // An effect always runs once on mount regardless of its dependencies, but
  // alertFilter is already in sync with the URL at that point (it was seeded
  // from the same search string above) -- skip that first run so mounting on
  // a URL like ?alert_id=5&page=4 doesn't deselect/reset page as though the
  // alert filter had just changed.
  const isInitialAlertMountRef = useRef(true);

  useEffect(() => {
    // Only rewrite when the alert params actually change -- on mount they
    // usually don't, and re-encoding the query string for nothing would churn
    // any other params already on the URL.
    const url = new URL(window.location.href);
    const search = alertFilterToSearch(url.search, alertFilter);
    if (search !== url.searchParams.toString()) {
      url.search = search;
      window.history.replaceState({}, '', url.toString());
    }

    if (isInitialAlertMountRef.current) {
      isInitialAlertMountRef.current = false;
      return;
    }

    // Whether a row still matches an alert filter can't be evaluated locally the
    // way rowMatchesFilterModel does for column filters -- the row's alerts
    // carry no BillingAlert ids -- so drop the selection outright rather than
    // leaving rows selected that the grid is about to stop showing.
    gridRef.current?.api?.deselectAll();
    setPage(1);
  }, [alertFilter]);

  // Column filters and sorts live in the grid, not in React state, so they're
  // mirrored onto the URL by hand: read once on mount to seed the grid, and
  // written back on every filter/sort change. Makes a filtered, sorted view
  // reloadable and shareable the same way the alert filter already is.
  const initialGridState = useMemo(() => {
    const search = window.location.search;
    return {
      filterModel: {
        ...filterModelFromSearch(search),
        ...billingItemIdFilterFromSearch(search)
      },
      sortModel: sortModelFromSearch(search)
    };
  }, []);

  const syncUrlFromGrid = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;

    const sortModel: SortModelItem[] = api
      .getColumnState()
      .filter((column) => column.sort === 'asc' || column.sort === 'desc')
      .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
      .map((column) => ({ colId: column.colId, sort: column.sort as 'asc' | 'desc' }));

    const url = new URL(window.location.href);
    const search = gridStateToSearch(url.search, api.getFilterModel() as FilterModel, sortModel);
    if (search === url.searchParams.toString()) return;

    url.search = search;
    window.history.replaceState({}, '', url.toString());
  }, []);

  const billingItemsQuery = useQuery({
    queryKey: ['billingItems', page, gridFilters, sortModel, alertFilter],
    queryFn: () => {
      const alertParams = alertFilterParams(alertFilter);
      const combined = { ...gridFilters, ...alertParams };
      const filters = Object.keys(combined).length > 0 ? combined : undefined;
      const ordering = translateSortModel(sortModel);

      return billingItemRepo.listBillingItems({ page: page.toString(), ordering, filters });
    }
  });
  const refetchBillingItems = billingItemsQuery.refetch;

  useEffect(() => {
    if (!billingItemsQuery.isError) return;

    const error = billingItemsQuery.error as AxiosError;
    // Rows removed from a later page (e.g. bulk delete) can shrink total_pages
    // below the current page, and DRF's PageNumberPagination 404s rather than
    // clamping. Recover by returning to page 1 instead of leaving the grid
    // stuck on a page that can never load.
    const isInvalidPage =
      error.response?.status === 404 &&
      (error.response?.data as { detail?: string } | undefined)?.detail === 'Invalid page.';

    if (isInvalidPage && page !== 1) {
      setPage(1);
      return;
    }

    addMessage(mapAxiosErrorMessages(error));
  }, [billingItemsQuery.isError, billingItemsQuery.error, addMessage, page]);

  const rowData = useMemo(
    () =>
      (billingItemsQuery.data?.results || []).filter(
        (item: BillingReportRow | null): item is BillingReportRow => item != null
      ),
    [billingItemsQuery.data]
  );

  const totalCount = billingItemsQuery.data?.count;
  const totalPages = billingItemsQuery.data?.total_pages ?? 1;

  const columnDefs: ColDef[] = useMemo(
    () => createBillingRowColumnDefs(host, { serverSide: true }),
    [host]
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      floatingFilter: false
    }),
    []
  );

  const onGridReady = (params: GridReadyEvent) => {
    // Restore the URL's filters and sorts into the grid's own UI state (filter
    // icons, sort arrows) on mount -- the actual page fetch is driven by
    // gridFilters/sortModel React state, seeded from the same URL separately.
    if (Object.keys(initialGridState.filterModel).length > 0) {
      params.api.setFilterModel(initialGridState.filterModel);
    }
    if (initialGridState.sortModel.length > 0) {
      params.api.applyColumnState({
        state: initialGridState.sortModel.map((item, index) => ({
          colId: item.colId,
          sort: item.sort,
          sortIndex: index
        })),
        defaultState: { sort: null }
      });
    }
  };

  const alertSummaryQuery = useQuery({
    queryKey: ['billingItemAlertSummary', gridFilters],
    // Deliberately keyed on the column filters only, without the alert filter:
    // drilling into one alert shouldn't collapse the panel to that single
    // message, since the panel is how you get back out to the others.
    queryFn: () => billingItemRepo.getSummary(gridFilters),
    enabled: showAlertSummary
  });

  // The lookup sheet ID is static config, so it's fetched once for the header
  // link rather than being refetched alongside the grid's data.
  const billingConfigQuery = useQuery({
    queryKey: ['billingConfig'],
    queryFn: () => billingItemRepo.getBillingConfig(),
    staleTime: Infinity
  });
  const billingLookupSheetId = billingConfigQuery.data?.billing_lookup_sheet_id;

  // Billing lookups (rates, service-line mappings) are cached globally rather
  // than per report, so flushing them from here has the same effect as flushing
  // from a report -- worth doing after editing the lookup sheet, before
  // refreshing rows.
  const handleFlushLookups = useCallback(async () => {
    setLoading((prev) => ({ ...prev, flushingLookups: true }));
    try {
      const result = await billingItemRepo.flushLookups();
      addMessage({
        level: 'success',
        text: result.message || 'All billing lookups have been flushed.'
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setErrorMessage(`Failed to flush lookups: ${extractErrorDetail(error)}`);
      } else {
        setErrorMessage('Failed to flush lookups');
      }
    } finally {
      setLoading((prev) => ({ ...prev, flushingLookups: false }));
    }
  }, [billingItemRepo, addMessage, setErrorMessage]);

  const filterByAlert = useCallback((message: AlertMessageCount) => {
    if (!message.alert_id) return;

    setAlertFilter({ alertId: message.alert_id, description: message.description });
    gridRef.current?.api?.ensureIndexVisible(0);
  }, []);

  // onSelectionChanged fires once per selection-changing action (row click,
  // select-all, API-driven changes), unlike onRowSelected which fires per row
  // and can under-report the final selection when many rows change at once.
  const onSelectionChanged = useCallback(() => {
    if (!gridRef.current || !gridRef.current.api) return;

    const rows = gridRef.current.api.getSelectedRows();
    setHasSelection(rows.length > 0);
    setSelectedRows(rows);
    showSelectedRowInDugout(rows);
  }, [showSelectedRowInDugout]);

  // Filters apply going forward only -- a row already selected before the
  // change stays selected as long as it still matches the new filter model,
  // and is dropped from the selection the moment it wouldn't be (evaluated
  // locally against the row data already held in selection state, see
  // rowMatchesFilterModel).
  const deselectRowsHiddenByFilter = useCallback(() => {
    if (!gridRef.current || !gridRef.current.api) return;

    const filterModel = gridRef.current.api.getFilterModel() as FilterModel;
    const nodesToDeselect = gridRef.current.api
      .getSelectedNodes()
      .filter((node) => node.data && !rowMatchesFilterModel(node.data, filterModel));

    if (nodesToDeselect.length === 0) return;

    gridRef.current.api.setNodesSelected({ nodes: nodesToDeselect, newValue: false });
    onSelectionChanged();
  }, [onSelectionChanged]);

  const onFilterChanged = useCallback(() => {
    // Evaluate the outgoing selection against the new filter model before
    // resetting to page 1 -- the page reset tears down the row nodes' data,
    // so this has to run first while the previously-selected rows' data is
    // still intact.
    deselectRowsHiddenByFilter();

    // Re-read the grid's filter model into state: it drives the query for
    // the current page and the alert summary query, and re-renders the
    // "current filters" display (FilterExplanation, which reads
    // gridRef.current directly) immediately rather than waiting on the next
    // fetch to update totalCount.
    const newFilters = translateFilterModel(gridRef.current?.api?.getFilterModel() || {});

    // onGridReady applying the URL's restored filter model also fires this
    // event -- skip the page reset then, since the restored filters already
    // match gridFilters (both came from the same URL) and the URL's own page
    // number should win instead of snapping back to page 1.
    if (JSON.stringify(newFilters) !== JSON.stringify(gridFilters)) {
      setPage(1);
    }
    setGridFilters(newFilters);

    syncUrlFromGrid();
  }, [deselectRowsHiddenByFilter, syncUrlFromGrid, gridFilters]);

  const onSortChanged = useCallback(() => {
    const columnState: ColumnState[] = gridRef.current?.api.getColumnState() || [];
    const newSortModel = columnState
      .filter((column): column is ColumnState & { sort: 'asc' | 'desc' } => !!column.sort)
      .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
      .map((column) => ({ colId: column.colId, sort: column.sort }));

    // As in onFilterChanged, onGridReady restoring the URL's sort model also
    // fires this event -- skip the page reset when it's a no-op relative to
    // what's already in state, so the URL's page number isn't clobbered.
    if (JSON.stringify(newSortModel) !== JSON.stringify(sortModel)) {
      setPage(1);
    }
    setSortModel(newSortModel);
    syncUrlFromGrid();
  }, [syncUrlFromGrid, sortModel]);

  const handleClearFilter = useCallback(
    (field: string) => {
      if (!gridRef.current?.api) return;
      const { [field]: _removed, ...rest } = gridRef.current.api.getFilterModel();
      gridRef.current.api.setFilterModel(rest);
      onFilterChanged();
    },
    [onFilterChanged]
  );

  // Added/removed rows can shift which items belong on the current page (and
  // change totalCount), so re-fetch the page rather than patching the grid's
  // row nodes directly for those. An update can shift membership too --
  // e.g. submitting an item it no longer matches a "not yet submitted"
  // filter -- so re-fetch whenever an updated row would no longer satisfy
  // the active column filters. Whether a row still matches an alert filter
  // can't be evaluated locally the same way (see the alertFilter effect
  // above), so any update while one is active re-fetches too, rather than
  // risking a stale row staying on the page. Only updates that provably
  // don't change membership apply straight to the row node.
  const partialUpdateAgGrid = useCallback(
    (transaction: RowDataTransaction) => {
      if (!gridRef.current) return;

      const api = gridRef.current.api;
      const updates = (transaction.update || []) as BillingReportRow[];
      const filterModel = api.getFilterModel() as FilterModel;
      const updateChangesMembership =
        updates.length > 0 &&
        (!!alertFilter || !updates.every((row) => rowMatchesFilterModel(row, filterModel)));

      if (transaction.add?.length || transaction.remove?.length || updateChangesMembership) {
        refetchBillingItems();
      } else {
        updates.forEach((row) => {
          const node = api.getRowNode(row.billing_item_id);
          if (node) node.setData(row);
        });
      }

      onSelectionChanged();
    },
    [refetchBillingItems, onSelectionChanged, alertFilter]
  );

  const setRowFlags = useCallback((ids: string[], flags: Partial<BillingReportRow>) => {
    if (!gridRef.current) return;

    const updatedNodes = ids
      .map((id) => gridRef.current?.api.getRowNode(id))
      .filter((node): node is IRowNode<BillingReportRow> => !!node?.data);

    updatedNodes.forEach((node) => {
      node.updateData({ ...node.data, ...flags } as BillingReportRow);
    });

    // The status column's value is bound to `errors`, which isn't part of
    // `flags` -- ag-grid's cell-level change detection sees no value change
    // and skips re-rendering, so isRefreshing/isSubmitting spinners wouldn't
    // show until the row is recycled by scrolling. Force the refresh.
    gridRef.current.api.refreshCells({
      rowNodes: updatedNodes,
      columns: ['errors'],
      force: true
    });
  }, []);

  // Subscribe to the dedicated billing-items Ably channel for real-time
  // updates from bulk refresh/submit background tasks -- since a selection
  // here can span multiple billing reports, these can't be scoped to a
  // single report's channel like BillingReportResultApp's.
  useChannelData({
    channelId: billingItemsChannelName(),
    channelName: 'billing-items',
    messageCallback: async (message: Message) => {
      const data = JSON.parse(message.data as string) as BillingReportEvent;

      if (isRefreshStartedEvent(data)) {
        setRowFlags(data.ids, { isRefreshing: true });
      }

      if (isSubmitStartedEvent(data)) {
        setRowFlags(data.ids, { isSubmitting: true });
      }

      if (isRefreshCompleteEvent(data) || isSubmitCompleteEvent(data)) {
        const idsToFetch = [...(data.add_ids || []), ...(data.update_ids || [])];
        const fetchedRows = await billingItemRepo.fetchRowsByIds(idsToFetch);

        const addIdSet = new Set(data.add_ids || []);
        const updateIdSet = new Set(data.update_ids || []);
        const addRows: BillingReportRow[] = [];
        const updateRows: BillingReportRow[] = [];
        fetchedRows.forEach((row) => {
          if (addIdSet.has(row.billing_item_id)) {
            addRows.push(row);
          } else if (updateIdSet.has(row.billing_item_id)) {
            updateRows.push(row);
          }
        });

        const removeRows = (data.remove_ids || []).map((id) => ({ billing_item_id: id }));

        partialUpdateAgGrid({
          add: addRows,
          update: updateRows,
          remove: removeRows
        });

        // skip_ids are requests that couldn't be resolved to a report result
        // server-side -- they never show up in add/update/remove, so without
        // this their row would stay stuck showing a refreshing/submitting
        // spinner forever.
        if (data.skip_ids?.length) {
          if (isRefreshCompleteEvent(data)) {
            setRowFlags(data.skip_ids, { isRefreshing: false });
          } else {
            setRowFlags(data.skip_ids, { isSubmitting: false });
          }
        }
      }

      if (isRefreshFailedEvent(data)) {
        setRowFlags(data.ids, { isRefreshing: false });
      }

      if (isSubmitFailedEvent(data)) {
        setRowFlags(data.ids, { isSubmitting: false });
      }
    }
  });

  const checkRowsAreAllOnHold = () => {
    if (!gridRef.current || !gridRef.current.api) return false;

    const rows = gridRef.current.api.getSelectedRows();
    return rows.length > 0 && rows.every((row) => row.manually_held_at);
  };

  const handleAssociatePanelDismiss = () => {
    setShowAssociateClaimPanel(false);

    if (!gridRef.current) return;
    linkedServiceLines.forEach((row) => {
      const node = gridRef.current?.api.getRowNode(row.billingReportRow.billing_item_id);
      if (node) {
        node.setDataValue('pending_cms_1500_healthie_id', null);
      }
    });

    setLinkedServiceLines([]);
  };

  const openAssociateClaimPanel = () => {
    if (showAssociateClaimPanel) {
      handleAssociatePanelDismiss();
    } else {
      setShowAssociateClaimPanel(true);
    }
  };

  const handleClaimLoad = (claim: Cms1500) => {
    if (!gridRef.current) return;

    gridRef.current.api.setFilterModel({
      person_id: {
        filterType: 'text',
        type: 'equals',
        filter: claim.patient
      }
    });
  };

  const handlePendingAssociation = (newLinkedServiceLines: LinkedServiceLine[]) => {
    if (!gridRef.current) return;

    newLinkedServiceLines.forEach((row) => {
      const node = gridRef.current?.api.getRowNode(row.billingReportRow.billing_item_id);
      if (node) {
        node.setDataValue('pending_cms_1500_healthie_id', row.serviceLine.cms1500);
      }
    });

    gridRef.current.api.deselectAll();
  };

  const removeLinkedServiceLine = (billingReportRow: BillingReportRow) => {
    if (!gridRef.current) return;

    const billingItemId = billingReportRow.billing_item_id;
    if (!billingItemId) return;

    const node = gridRef.current.api.getRowNode(billingItemId);
    if (node) {
      node.setDataValue('pending_cms_1500_healthie_id', null);
    }

    setLinkedServiceLines(
      linkedServiceLines.filter(
        (linkedServiceLine) => linkedServiceLine.billingReportRow.billing_item_id !== billingItemId
      )
    );
  };

  const highlightBillingRow = (billingReportRow: BillingReportRow) => {
    if (!gridRef.current) return;

    gridRef.current.api.deselectAll();
    const node = gridRef.current.api.getRowNode(billingReportRow.billing_item_id);
    if (node) {
      gridRef.current.api.setNodesSelected({ nodes: [node], newValue: true });
    }
  };

  const handleSaveAssociation = async (newLinkedServiceLines: LinkedServiceLine[]) => {
    const linkedItems = newLinkedServiceLines
      .filter((row) => !!row.serviceLine.id)
      .map((row) => ({
        service_id: row.billingReportRow.service_id,
        cms_1500_cpt_code_id: row.serviceLine.id as string
      }));

    setLoading((prev) => ({ ...prev, associating: true }));
    try {
      const transaction = await billingItemRepo.linkItems(linkedItems);
      handleAssociatePanelDismiss();
      partialUpdateAgGrid(transaction);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setErrorMessage(`Failed to link service line ${extractErrorDetail(error)}`);
      } else {
        setErrorMessage(JSON.stringify(error));
      }
    } finally {
      setLoading((prev) => ({ ...prev, associating: false }));
    }
  };

  const markSelectedRowsOnHold = async (
    reason: string,
    note: string | null,
    rows: BillingReportRow[]
  ) => {
    if (!rows.length) return;

    setLoading((prev) => ({ ...prev, holding: true }));

    const hasCms1500 = rows.some((row) => !!row.cms_1500_healthie_id);
    if (hasCms1500) {
      setErrorMessage('Cannot hold a row with a CMS 1500 ID');
      setLoading((prev) => ({ ...prev, holding: false }));
      return;
    }

    const payload: BulkBillingItemFlagPayload = {
      type: BillingItemFlagType.MANUALLY_HELD,
      reason,
      note,
      billingItemIds: rows.map((row) => row.billing_item_id)
    };

    try {
      await billingItemFlagRepository.addFlag(payload);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setErrorMessage(`Failed to hold service line ${extractErrorDetail(error)}`);
      } else {
        setErrorMessage(JSON.stringify(error));
      }
    } finally {
      setLoading((prev) => ({ ...prev, holding: false }));
    }
  };

  const markSelectedRowsAsIgnoreError = async (flag: BulkIgnoreErrorsFlag) => {
    const { reason, note, alerts, rows, type } = flag;
    if (!rows.length) return;

    setLoading((prev) => ({ ...prev, ignoringErrors: true }));

    const hasCms1500 = rows.some((row) => !!row.cms_1500_healthie_id);
    if (hasCms1500) {
      setErrorMessage('Cannot ignore errors on row with a CMS 1500 ID');
      setLoading((prev) => ({ ...prev, ignoringErrors: false }));
      return;
    }

    const payload: BulkBillingItemFlagPayload = {
      type,
      reason,
      note,
      alerts,
      billingItemIds: rows.map((row) => row.billing_item_id)
    };

    try {
      await billingItemFlagRepository.addFlag(payload);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setErrorMessage(`Failed to ignore errors for service line ${extractErrorDetail(error)}`);
      } else {
        setErrorMessage(JSON.stringify(error));
      }
    } finally {
      setLoading((prev) => ({ ...prev, ignoringErrors: false }));
    }
  };

  const refreshSelectedRows = useCallback(async () => {
    if (!gridRef.current || !gridRef.current.api) return;

    const rows = gridRef.current.api.getSelectedRows();
    if (!rows.length) return;

    const billingItemIds = rows.map((row) => row.billing_item_id);
    setRowFlags(billingItemIds, { isRefreshing: true });
    setLoading((prev) => ({ ...prev, refreshing: true }));
    try {
      const result = await billingItemRepo.refreshRows(billingItemIds);
      addMessage({ level: 'info', text: result.message });
    } catch (error) {
      setRowFlags(billingItemIds, { isRefreshing: false });
      if (axios.isAxiosError(error)) {
        setErrorMessage(`Failed to refresh service line ${extractErrorDetail(error)}`);
      } else {
        setErrorMessage(JSON.stringify(error));
      }
    } finally {
      setLoading((prev) => ({ ...prev, refreshing: false }));
    }
  }, [billingItemRepo, setErrorMessage, setRowFlags, addMessage]);

  const handleManualHold = async (reason: string, note: string, rows: BillingReportRow[]) => {
    await markSelectedRowsOnHold(reason, note, rows);
    setShowManualHoldInput(false);
    await refreshSelectedRows();
  };

  const handleIgnoreError = async (flag: BulkIgnoreErrorsFlag) => {
    await markSelectedRowsAsIgnoreError(flag);
    setShowIgnoreErrorInput(false);
    await refreshSelectedRows();
  };

  const handleClearFlag = async (type: string, onlyForUser: boolean, rows: BillingReportRow[]) => {
    if (type === BillingItemFlagType.MANUALLY_HELD) {
      setLoading((prev) => ({ ...prev, clearingHold: true }));
    }

    try {
      await billingItemFlagRepository.clearFlags({
        type,
        onlyForUser,
        billingItemIds: rows.map((row) => row.billing_item_id)
      });
    } finally {
      if (type === BillingItemFlagType.MANUALLY_HELD) {
        setLoading((prev) => ({ ...prev, clearingHold: false }));
      }
    }

    await refreshSelectedRows();
  };

  const handleManualHoldClick = async () => {
    if (!gridRef.current || !gridRef.current.api) return;
    const rows = gridRef.current.api.getSelectedRows();
    if (checkRowsAreAllOnHold()) {
      await handleClearFlag(BillingItemFlagType.MANUALLY_HELD, false, rows);
    } else {
      setShowManualHoldInput(true);
    }
  };

  const removeSelectedRows = async () => {
    if (!gridRef.current) return;

    setLoading((prev) => ({ ...prev, deleting: true }));

    const nodes = gridRef.current.api.getSelectedNodes();
    const rows: BillingReportRow[] = nodes
      .map((row) => row.data)
      .filter((row): row is BillingReportRow => !!row);

    const hasCms1500 = rows.some((row) => !!row.cms_1500_healthie_id);
    if (hasCms1500) {
      setErrorMessage('Cannot delete rows with CMS 1500 ID');
      setLoading((prev) => ({ ...prev, deleting: false }));
      return;
    }

    try {
      await billingItemRepo.updateRows(rows.map((row) => ({ action: 'remove', row })));
      await billingItemsQuery.refetch();
      onSelectionChanged();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setErrorMessage(`Failed to remove service line ${extractErrorDetail(error)}`);
      } else {
        setErrorMessage(JSON.stringify(error));
      }
    } finally {
      setLoading((prev) => ({ ...prev, deleting: false }));
    }
  };

  const submitSelectedRows = async () => {
    if (!gridRef.current || !gridRef.current.api) return;

    const rows = gridRef.current.api.getSelectedRows();
    if (!rows.length) return;

    const billingItemIds = rows.map((row) => row.billing_item_id);
    setRowFlags(billingItemIds, { isSubmitting: true });
    setLoading((prev) => ({ ...prev, submitting: true }));
    try {
      const result = await billingItemRepo.submitSelectedRows(rows);
      addMessage({ level: 'success', text: result.message });
    } catch (error) {
      setRowFlags(billingItemIds, { isSubmitting: false });
      if (axios.isAxiosError(error)) {
        setErrorMessage(`Failed to submit service line ${extractErrorDetail(error)}`);
      } else {
        setErrorMessage(JSON.stringify(error));
      }
    } finally {
      setLoading((prev) => ({ ...prev, submitting: false }));
    }
  };

  const startEditingSelectedRows = () => {
    setEditingSelectedRows(true);
  };

  const afterSavingEditingSelectedRows = ({ refresh }: { refresh: boolean }) => {
    if (refresh) refreshSelectedRows();
    setEditingSelectedRows(false);
  };

  const rowCanBeSubmitted = (row: BillingReportRow): boolean => {
    return (
      (!row.errors || Object.keys(row.errors).length === 0) &&
      !row.manually_held_at &&
      !row.cms_1500_healthie_id &&
      !!row.claim_group_id
    );
  };

  const hasSubmittableRowsSelected = useMemo(() => {
    return hasSelection && selectedRows.every(rowCanBeSubmitted);
  }, [selectedRows, hasSelection]);

  return (
    <BillingReportProvider
      apiToken=""
      reportId={-1}
      reportEndAt={null}
      healthieUrl={healthieLink({ host })}
      messages={[]}
      setErrorMessage={setErrorMessage}
      setMessages={setMessages}
      gridRef={gridRef}>
      <BillingFlagReasonProvider>
        <AssociateBillingRowsProvider
          value={{
            removeLinkedServiceLine,
            linkedServiceLines,
            setLinkedServiceLines,
            highlightBillingRow
          }}>
          <div className="min-h-screen bg-slate-100 p-4">
            <div className="w-full bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4">
                <div className="flex items-baseline gap-3">
                  <PageHeader headingText="Billing Items" />
                  <span className="text-sm text-gray-400">{totalCount ?? '—'} items</span>
                </div>
                <div className="flex items-center gap-3">
                  <AvatarStackForSpace />

                  <Dropdown
                    buttonText={<FontAwesomeIcon icon={faEllipsisVertical} />}
                    showChevronIcon={false}
                    customButtonClassName="inline-flex items-center justify-center h-[34px] w-[34px] rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800"
                    items={[
                      ...(billingLookupSheetId
                        ? [
                            <a
                              key="view-lookup"
                              href={`https://docs.google.com/spreadsheets/d/${billingLookupSheetId}`}
                              target="_blank"
                              rel="noreferrer"
                              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700">
                              <FontAwesomeIcon className="mr-2" icon={faTable} />
                              View Billing Lookup
                            </a>
                          ]
                        : []),
                      <button
                        key="flush-lookups"
                        type="button"
                        disabled={loading.flushingLookups}
                        onClick={handleFlushLookups}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50">
                        <FontAwesomeIcon
                          className={`mr-2 ${loading.flushingLookups ? 'animate-spin' : ''}`}
                          icon={loading.flushingLookups ? faSpinner : faToilet}
                        />
                        {loading.flushingLookups ? 'Flushing...' : 'Flush Lookups'}
                      </button>
                    ]}
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 flex-wrap px-5 pb-4">
                <BillingReportFilters />

                <Button
                  className="flex items-center"
                  color="white"
                  size="sm"
                  onClick={() => setShowAlertSummary(!showAlertSummary)}>
                  <FontAwesomeIcon
                    icon={showAlertSummary ? faChevronDown : faChevronRight}
                    className="mr-1"
                  />
                  Alert Summary
                </Button>
              </div>

              {(Object.keys(gridRef.current?.api?.getFilterModel() || {}).length > 0 ||
                alertFilter) && (
                <div className="flex items-center gap-2 flex-wrap bg-gray-50 border-y border-gray-100 px-5 py-3">
                  <span className="text-xs font-semibold text-gray-400 tracking-wide uppercase">
                    Filters
                  </span>
                  {Object.entries(gridRef.current?.api?.getFilterModel() || {}).map(
                    ([field, value]) => (
                      <FilterExplanation
                        key={`filter-${field}`}
                        field={field}
                        filterModel={value}
                        gridRef={gridRef}
                        onClear={handleClearFilter}
                      />
                    )
                  )}
                  {/* The alert filter isn't in the grid's filter model, so it
                      gets its own chip rather than a FilterExplanation. */}
                  {alertFilter && (
                    <span className="inline-flex items-center gap-2 h-6 px-2 rounded-md bg-white border border-gray-200 text-xs text-gray-600">
                      Alert: {alertFilter.description}
                      <button type="button" onClick={() => setAlertFilter(null)}>
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </span>
                  )}
                </div>
              )}

              {showAlertSummary && (
                <div className="px-5 py-3 border-b border-gray-100">
                  <AlertSummaryPanel
                    summary={alertSummaryQuery.data}
                    isLoading={alertSummaryQuery.isPending}
                    isError={alertSummaryQuery.isError}
                    onFilterByAlert={filterByAlert}
                  />
                </div>
              )}

              {hasSelection && (
                <div className="flex items-center justify-between gap-3 flex-wrap bg-indigo-50 border-b border-indigo-100 px-5 py-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center h-6 px-2 rounded-md bg-indigo-600 text-white text-xs font-semibold">
                      {selectedRows.length} selected
                    </span>

                    <Button
                      className="flex items-center"
                      color="white"
                      size="sm"
                      disabled={loading.holding || loading.clearingHold}
                      onClick={handleManualHoldClick}>
                      {loading.holding || loading.clearingHold ? (
                        <>
                          <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                          {loading.holding ? 'Holding...' : 'Clearing Hold...'}
                        </>
                      ) : (
                        <>{checkRowsAreAllOnHold() ? 'Clear Hold' : 'Hold'}</>
                      )}
                    </Button>

                    <Button
                      className="flex items-center"
                      color="white"
                      size="sm"
                      disabled={loading.ignoringErrors}
                      onClick={() => setShowIgnoreErrorInput(true)}>
                      {loading.ignoringErrors ? (
                        <>
                          <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                          Ignoring Errors...
                        </>
                      ) : (
                        <>Ignore Errors</>
                      )}
                    </Button>

                    <Button
                      className="flex items-center"
                      color="white"
                      size="sm"
                      onClick={openAssociateClaimPanel}>
                      <FontAwesomeIcon className="mr-2" icon={faLink} />
                      Associate Claim
                    </Button>

                    <Button
                      className="flex items-center"
                      color="white"
                      size="sm"
                      onClick={startEditingSelectedRows}>
                      <FontAwesomeIcon className="mr-2" icon={faPlus} />
                      Edit
                    </Button>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      className="flex items-center"
                      color="white"
                      size="sm"
                      disabled={loading.refreshing}
                      onClick={refreshSelectedRows}>
                      {loading.refreshing ? (
                        <>
                          <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                          Refreshing...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon className="mr-2" icon={faRefresh} />
                          Refresh
                        </>
                      )}
                    </Button>

                    <Button
                      className="flex items-center"
                      color="blue"
                      size="sm"
                      disabled={!hasSubmittableRowsSelected || loading.submitting}
                      onClick={submitSelectedRows}>
                      {loading.submitting ? (
                        <>
                          <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon className="mr-2" icon={faUpload} />
                          Submit
                        </>
                      )}
                      <Tooltip
                        tooltipContent={
                          <p className="text-sm">
                            This will submit the selected rows to Healthie. This will only submit
                            rows that are valid, and have a claim group ID.
                          </p>
                        }>
                        <FontAwesomeIcon className="ml-2" icon={faInfoCircle} />
                      </Tooltip>
                    </Button>

                    <Dropdown
                      buttonText={<FontAwesomeIcon icon={faEllipsis} />}
                      showChevronIcon={false}
                      customButtonClassName="inline-flex items-center justify-center h-[34px] w-[34px] rounded-lg bg-white border border-indigo-200 text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800"
                      items={[
                        <button
                          key="remove"
                          type="button"
                          disabled={loading.deleting}
                          onClick={removeSelectedRows}
                          className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700">
                          {loading.deleting ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                              Removing...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon className="mr-2" icon={faTrash} />
                              Remove
                            </>
                          )}
                        </button>
                      ]}
                    />
                  </div>
                </div>
              )}

              {showManualHoldInput && (
                <ManualHoldServiceLines
                  rows={selectedRows}
                  onSaveHold={handleManualHold}
                  onClearFlag={handleClearFlag}
                  onDismiss={() => setShowManualHoldInput(false)}
                />
              )}

              {showIgnoreErrorInput && (
                <IgnoreErrorServiceLines
                  rows={selectedRows}
                  onIgnoreError={handleIgnoreError}
                  onClearIgnoreErrors={handleClearFlag}
                  onDismiss={() => setShowIgnoreErrorInput(false)}
                />
              )}

              {showAssociateClaimPanel && (
                <AssociateServiceLines
                  rows={selectedRows}
                  onDismiss={handleAssociatePanelDismiss}
                  onClaimLoad={handleClaimLoad}
                  onSaveAssociation={handleSaveAssociation}
                  onPendingAssociation={handlePendingAssociation}
                />
              )}

              {editingSelectedRows && (
                <RowEditor
                  rows={selectedRows}
                  onDismiss={() => setEditingSelectedRows(false)}
                  afterSave={afterSavingEditingSelectedRows}
                />
              )}

              <div className="w-full border-t border-gray-200">
                <div className="w-full billing-items-grid">
                  <AgGridReact<BillingReportRow>
                    ref={gridRef}
                    rowData={rowData}
                    loading={billingItemsQuery.isFetching}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    enableBrowserTooltips={true}
                    popupParent={popupParent}
                    theme={
                      resolvedTheme === 'dark' ? gridTheme.withPart(colorSchemeDark) : gridTheme
                    }
                    onGridReady={onGridReady}
                    onFilterChanged={onFilterChanged}
                    onSortChanged={onSortChanged}
                    // Sorts accumulate rather than replace each other: sorting by
                    // service date and then by name keeps both keys, with the
                    // second breaking ties inside the first, and the whole set
                    // goes to the server as one comma-separated `ordering` value.
                    // Without this, ag-grid only adds a sort on ctrl/cmd-click and
                    // a plain click drops the sort you already had. Clicking a
                    // sorted column cycles asc -> desc -> unsorted, so a key is
                    // removed by clicking through it.
                    alwaysMultiSort={true}
                    rowBuffer={10}
                    enableCellTextSelection={true}
                    domLayout="normal"
                    maintainColumnOrder={true}
                    getRowId={(params) => params.data.billing_item_id}
                    rowSelection={{
                      mode: 'multiRow',
                      checkboxes: true,
                      // The grid only ever holds the current page's rows (see
                      // rowData/billingItemsQuery above), so the header checkbox
                      // selecting "all rows in the grid" is exactly "all rows on
                      // this page" -- unlike the old infinite row model, where
                      // ag-grid doesn't support headerCheckbox at all (error #129)
                      // and most rows weren't even fetched yet.
                      headerCheckbox: true,
                      enableClickSelection: true
                    }}
                    onSelectionChanged={onSelectionChanged}
                  />
                </div>
              </div>

              <div className="flex items-center justify-end px-5 py-3 border-t border-gray-200">
                <Paginator currentPage={page} totalPages={totalPages} onPageChange={setPage} />
              </div>
            </div>
          </div>
        </AssociateBillingRowsProvider>
      </BillingFlagReasonProvider>
    </BillingReportProvider>
  );
};

export default BillingItemsList;
